import { useCallback, useEffect, useRef, useState } from 'react'
import { Container } from 'typescript-ioc/es5'
import { NotificationScheduleService } from '../services/notification-schedule'
import { InsightsService } from '../services/insights'
import { AppState } from '../stores/app'
import { observe } from 'mobx'

export interface NotificationCardData {
    id: number
    notificationId: number | string
    title: string
    body?: string
    imageUrl?: string
    status: string
    sendTime: string
    clicks?: number
    ctrDecimal?: number
    source?: string
    channels?: string[]
    /** Raw audience object from the API (for AudienceBadge) */
    audience?: any
    /** Created by user name */
    createdBy?: string
    /** Originating feed sub-resource (present when source is FEED). Used for attribution. */
    feed?: { id: number; name: string } | null
    /** Notification ID for display */
    displayId?: string
    /** Delivery strategy label (e.g., "Fixed", "Immediate", "Subscriber Time Zone") */
    deliveryStrategy?: string
    /** Last modified timestamp (used for drafts) */
    lastEditedAt?: string
    /** Creation timestamp (used for drafts to distinguish "Created" vs "Edited") */
    createdAt?: string
    /** AI source from auto_suggested_fields_results (e.g., 'pushly_ai', 'page_analyzer') */
    aiSource?: string
}

interface NotificationFilters {
    statuses: string[]
    sources?: string[]
    segments?: Array<string | number>
    search?: string
    /** When false, the hook will not fetch data. Default: true */
    enabled?: boolean
}

interface UseNotificationsResult {
    data: NotificationCardData[]
    loading: boolean
    statsLoading: boolean
    error: Error | null
    refresh: () => void
    loadMore: () => void
    hasMore: boolean
}

const PAGE_SIZE = 10

export function useNotifications(filters: NotificationFilters): UseNotificationsResult {
    const scheduleService = useRef(Container.get(NotificationScheduleService)).current
    const insightsService = useRef(Container.get(InsightsService)).current
    const appState = useRef(Container.get(AppState)).current

    const [data, setData] = useState<NotificationCardData[]>([])
    const [loading, setLoading] = useState(true)
    const [statsLoading, setStatsLoading] = useState(false)
    const [error, setError] = useState<Error | null>(null)
    const [page, setPage] = useState(1)
    const [hasMore, setHasMore] = useState(true)

    const fetchNotifications = useCallback(
        async (pageNum: number, append: boolean = false) => {
            const domainId = appState.currentDomain?.id
            if (!domainId) return
            if (filters.enabled === false) return

            setLoading(true)
            setError(null)

            try {
                const statuses = filters.statuses.length > 0 ? filters.statuses.join(',') : undefined
                const source =
                    filters.sources && filters.sources.length > 0 ? filters.sources.join(',') : 'manual,trigger,feed'

                const res = await scheduleService.fetchAllByDomainId(domainId, {
                    query: {
                        limit: PAGE_SIZE,
                        page: pageNum,
                        source,
                        includeSegments: true,
                        ...(statuses ? { status: statuses } : {}),
                        ...(filters.search ? { search: filters.search } : {}),
                        ...(filters.segments?.length ? { segment_ids: filters.segments.join(',') } : {}),
                    },
                    cancellationKey: 'mv.notif-list.fetch',
                    showLoadingScreen: false,
                })

                if (res.ok) {
                    const schedules: any[] = res.data ?? []

                    const mapped: NotificationCardData[] = schedules.map((s: any) => {
                        // Derive delivery strategy label
                        const spec = s.delivery_spec
                        let deliveryStrategy: string | undefined
                        if (spec) {
                            const window = spec.window
                            const type = spec.type
                            if (window === 'hold_out') deliveryStrategy = 'Hold Out'
                            else if (window === 'informed') deliveryStrategy = 'Informed'
                            else if (type === 'immediate') deliveryStrategy = 'Immediate'
                            else if (window === 'standard') deliveryStrategy = 'Fixed'
                            else if (window === 'timezone') deliveryStrategy = 'Subscriber Time Zone'
                        }

                        // Extract AI source from auto_suggested_fields_results
                        const aiResults = s.template?.channels?.default?.auto_suggested_fields_results
                        let aiSource: string | undefined
                        if (aiResults) {
                            if (aiResults.source) {
                                aiSource = aiResults.source
                            } else if (aiResults.title || aiResults.body || aiResults.image_url) {
                                aiSource = 'page_analyzer'
                            }
                        }

                        return {
                            id: s.id,
                            notificationId: s.notification_id,
                            title: s.template?.channels?.default?.title ?? 'Untitled',
                            body: s.template?.channels?.default?.body,
                            imageUrl: s.template?.channels?.default?.image_url,
                            status: s.status,
                            sendTime: spec?.send_date_utc ?? s.created_at,
                            clicks: s.clicks,
                            ctrDecimal: s.ctr_decimal,
                            source: s.source,
                            channels: s.channels,
                            audience: s.audience,
                            createdBy: s.created_by,
                            feed: s.feed ?? null,
                            displayId: s.notification_id ? String(s.notification_id) : undefined,
                            deliveryStrategy,
                            aiSource,
                        }
                    })

                    if (append) {
                        setData((prev) => [...prev, ...mapped])
                    } else {
                        setData(mapped)
                    }

                    setHasMore(mapped.length >= PAGE_SIZE)

                    // Fetch stats for delivered notifications (clicks/CTR)
                    // Stats are not on the schedule response — fetched separately via insights API
                    const notifIds = mapped.filter((m) => m.notificationId).map((m) => Number(m.notificationId))

                    if (notifIds.length > 0) {
                        setStatsLoading(true)
                        try {
                            const stats = await insightsService.fetch(
                                {
                                    entity: 'notifications',
                                    date_preset: 'lifetime',
                                    date_increment: 'LIFETIME',
                                    breakdowns: ['notification'],
                                    fields: ['notification.id', 'impressions', 'clicks', 'ctr_decimal'],
                                    action_attribution: 'send_time',
                                    filters: [
                                        { field: 'domain.id', operator: 'eq', value: domainId },
                                        { field: 'notification.id', operator: 'in', value: notifIds },
                                    ],
                                } as any,
                                {
                                    cancellationKey: 'mv.notif-list.stats',
                                    scopedInsightsPath: 'insights',
                                },
                            )

                            if (stats && Array.isArray(stats)) {
                                const statsMap = new Map<string, { clicks: number; ctrDecimal: number }>()
                                stats.forEach((s: any) => {
                                    // The insights API returns notification.id as a dot-notation key
                                    const nId = s['notification.id'] ?? s.notification?.id ?? s.notification_id
                                    if (nId) {
                                        statsMap.set(String(nId), {
                                            clicks: s.clicks ?? 0,
                                            ctrDecimal: s.ctr_decimal ?? 0,
                                        })
                                    }
                                })

                                // Update data with stats
                                setData((prev) =>
                                    prev.map((item) => {
                                        const stat = statsMap.get(String(item.notificationId))
                                        if (stat) {
                                            return { ...item, clicks: stat.clicks, ctrDecimal: stat.ctrDecimal }
                                        }
                                        return item
                                    }),
                                )
                            }
                        } catch {
                            // Stats fetch failure is non-critical — cards still render without stats
                        } finally {
                            setStatsLoading(false)
                        }
                    }
                } else {
                    throw new Error('Failed to fetch notifications')
                }
            } catch (err) {
                // Preserve previous data on error
                setError(err instanceof Error ? err : new Error('Failed to fetch notifications'))
            } finally {
                setLoading(false)
            }
        },
        [
            appState,
            scheduleService,
            insightsService,
            filters.statuses,
            filters.sources,
            filters.segments,
            filters.search,
        ],
    )

    const refresh = useCallback(() => {
        setPage(1)
        fetchNotifications(1, false)
    }, [fetchNotifications])

    const loadMore = useCallback(() => {
        const nextPage = page + 1
        setPage(nextPage)
        fetchNotifications(nextPage, true)
    }, [page, fetchNotifications])

    // Fetch on mount and when domain or filters change
    // Note: fetchNotifications depends on filters AND appState.currentDomain (read inside),
    // so this effect re-runs on both filter changes and domain changes (via filter reset in the view).
    useEffect(() => {
        setPage(1)
        setData([])
        setError(null)
        setLoading(true)
        fetchNotifications(1, false)
    }, [fetchNotifications])

    return { data, loading, statsLoading, error, refresh, loadMore, hasMore }
}
